import React, { Fragment } from 'react';
import { StyleSheet, View, ScrollView } from 'react-native';
import { observer } from 'mobx-react';

import Text from '../shared/Text';
import Image from '../shared/Image';

import appStore from '../../stores/app';
import { getSlotDimensions, ThingImage } from './Body';

import { thingAttrValid, checkThingStability } from '../../utils/warriorUtils';
import { getThing, getDuration } from '../../utils/thingUtils';
import { t, getLocalValue } from '../../utils/appUtils';

import { THING_NEED_ITEMS, THING_GIVE_ITEMS, DURATION_IMMEDIATE } from '../../lib/constants';
import { PADDING, PADDING1, RED, pOrT, BASE_COLOR } from '../styleConstants';

import { WarriorThingType, WarriorType } from '../../lib/types';

export const styles = StyleSheet.create({
  wrapper: {
    width: '100%',
    padding: PADDING1,
    flexDirection: 'row',
  },
  itemTitle: {
    marginBottom: 5,
  },
  thingTitle: {
    fontSize: pOrT(14, 16),
  },
});

type Props = {
  warrior: WarriorType;
  warriorThingOrId: WarriorThingType | string;
  index?: number;
  tooltip?: boolean;
  actionsRenderer?: Function;
  checkPrice?: boolean;
  width?: number;
  renderMore?: (props: any) => any;
  modalInfo?: boolean;
};

const ThingItem = observer(
  ({
    warrior,
    warriorThingOrId,
    index = 0,
    tooltip,
    checkPrice,
    actionsRenderer,
    width,
    renderMore = () => null,
  }: Props) => {
    const isInfo = typeof warriorThingOrId === 'string';
    let warriorThing: WarriorThingType | undefined;
    let id;
    let stabilityAll;
    let stabilityLeft;
    let showStability = true;
    if (isInfo) {
      id = warriorThingOrId;
    } else {
      warriorThing = warriorThingOrId;

      id = warriorThing.thing;

      ({ stabilityAll, stabilityLeft } = warriorThing);

      showStability = !warriorThing.finishing;
    }

    const thing = getThing(appStore.initData.things, id);

    if (isInfo) {
      stabilityAll = thing.stability;
      stabilityLeft = thing.stabilityLeft || stabilityAll;
    }

    const DESC_WIDTH = 120;

    function rrenderItem(key: string, value: any, valid?: any, type?: any) {
      if (value == null) return null;

      return (
        <View style={{ flexDirection: 'row' }}>
          <Text
            key={key}
            style={{
              maxWidth: DESC_WIDTH + (type === 'desc' ? 20 : 0),
            }}
          >
            {key}
          </Text>
          <Text
            bold
            style={{
              marginLeft: 5,
              color: valid === false ? RED : BASE_COLOR,
            }}
          >
            {value}
          </Text>
        </View>
      );
    }

    const needItems = THING_NEED_ITEMS.map((attr) => {
      const label = t(`hero.${attr.replace('Need', '')}`);
      return rrenderItem(label, thing[attr], thingAttrValid(attr, thing, warrior));
    });

    const infoItems = THING_GIVE_ITEMS.map((item) => {
      const label = t(`hero.${item}`);
      return rrenderItem(label, thing[item] > 0 ? `+${thing[item]}` : null, undefined, 'desc');
    });

    if (thing.isTwoHands) {
      infoItems.push(rrenderItem(t('things.twoHands'), ' '));
    }

    if (thing.money) {
      infoItems.push(rrenderItem(t('things.money'), `+${thing.money}`));
    }

    if (thing.duration && thing.duration !== DURATION_IMMEDIATE) {
      infoItems.push(rrenderItem(t('things.duration'), `${thing.duration} ${t('min')}`));
    }

    if (thing.recoverHp) {
      infoItems.push(rrenderItem(t('things.recoverHp'), `+${thing.recoverHp}`));
    }

    if (thing.description) {
      infoItems.push(rrenderItem(getLocalValue(thing, 'description'), ' '));
    }
    const scrollHeight = tooltip ? undefined : 120;

    const InfoComp = tooltip ? View : ScrollView;

    const highlightStability = !isInfo;

    return (
      <View
        key={index + String(id)}
        nativeID={tooltip ? 'thing-tooltip' : undefined}
        style={[
          index > 0 && { marginTop: PADDING },
          { height: tooltip ? undefined : pOrT(195, 220), width: width || 467 },
          { minHeight: !tooltip ? undefined : 220 },
        ]}
      >
        <Image
          style={{
            width: width || 467,
            height: pOrT(195, 220),
            position: 'absolute',
          }}
          resizeMode="stretch"
          source={require('../../assets/images/paper3.png')}
        />
        <View style={styles.wrapper}>
          <View>
            <Text bold style={styles.thingTitle}>
              {getLocalValue(thing)}
            </Text>
            <View
              style={{
                alignContent: 'flex-start',
                flexDirection: 'row',
              }}
            >
              {rrenderItem(
                t('things.price'),
                thing.price,
                checkPrice ? warrior.money >= thing.price : undefined,
              )}
              <View style={{ marginLeft: thing.price == null ? 0 : 10 }}>
                {rrenderItem(t('things.weight'), thing.weight)}
              </View>
              <View style={{ marginLeft: 10 }}>{renderMore({ warriorThing, thing })}</View>
            </View>
            <View
              style={{
                flexDirection: 'row',
                marginTop: 5,
              }}
            >
              <View
                style={{
                  width: 80,
                  alignItems: 'center',
                  marginTop: 5,
                }}
              >
                <View
                  style={{
                    height: getSlotDimensions(thing.type, undefined).height,
                  }}
                >
                  <ThingImage
                    highlightWarning={highlightStability}
                    warriorThing={warriorThing as WarriorThingType}
                    thing={thing}
                  />
                </View>
                <View style={{ marginTop: 3, flexDirection: 'row' }}>
                  {showStability ? (
                    <Fragment>
                      <Text
                        bold
                        style={{
                          color:
                            highlightStability &&
                            checkThingStability(warriorThing as WarriorThingType, thing, 'all')
                              ? RED
                              : BASE_COLOR,
                        }}
                      >
                        {stabilityAll}
                      </Text>
                      <Text> / </Text>
                      <Text
                        bold
                        style={{
                          color:
                            highlightStability &&
                            checkThingStability(warriorThing as WarriorThingType, thing, 'left')
                              ? RED
                              : BASE_COLOR,
                        }}
                      >
                        {stabilityLeft}
                      </Text>
                    </Fragment>
                  ) : (
                    <Text>{getDuration(warriorThing!.finishing as number, t)}</Text>
                  )}
                </View>
              </View>
              <View
                style={{
                  width: DESC_WIDTH,
                  marginLeft: PADDING,
                }}
              >
                <Text bold style={styles.itemTitle}>
                  {t('things.requirements')}
                </Text>
                <InfoComp style={{ height: scrollHeight }}>{needItems}</InfoComp>
              </View>
              <View>
                <Text bold style={styles.itemTitle}>
                  {t('things.description')}
                </Text>
                <InfoComp style={{ height: scrollHeight }}>{infoItems}</InfoComp>
              </View>
            </View>
          </View>
          {actionsRenderer ? (
            <View
              style={{
                top: PADDING,
                position: 'absolute',
                right: PADDING * 2,
              }}
            >
              {actionsRenderer(thing)}
            </View>
          ) : null}
        </View>
      </View>
    );
  },
);

export const SmallSlot = ({ money, icon, size = pOrT(50, 60), iconSizeP = 0.7 }: any) => {
  const iconSize = size * iconSizeP;
  const iconSource = icon || require('../../assets/images/greed.png');
  return (
    <View style={{ width: size, height: size, position: 'relative' }}>
      <Image
        style={{ width: size, height: size, position: 'absolute', left: 0 }}
        source={require('../../assets/images/greyBack.png')}
      />
      <Image
        style={{ width: size, height: size, position: 'absolute' }}
        source={require('../../assets/images/armWrapper.png')}
      />
      <View style={{ justifyContent: 'center', alignItems: 'center', width: size, height: size }}>
        <Image
          style={{ width: iconSize, height: iconSize, resizeMode: 'contain' }}
          source={iconSource}
        />
      </View>
      {money !== undefined ? (
        <View style={{ position: 'absolute', bottom: 2, right: 2 }}>
          <View
            style={{
              backgroundColor: 'black',
              borderRadius: 4,
              paddingHorizontal: 3,
              justifyContent: 'center',
              alignItems: 'center',
              minWidth: 12,
              height: 12,
            }}
          >
            <Text bold style={{ color: 'white', fontSize: 8, backgroundColor: 'transparent' }}>
              {money}
            </Text>
          </View>
        </View>
      ) : null}
    </View>
  );
};

export default ThingItem;
